import { Component, Input, Output, ElementRef, EventEmitter, OnChanges, SimpleChanges, OnInit, OnDestroy, ViewChild, AfterViewChecked, NgZone } from '@angular/core';
import { from, fromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { getDocument, GlobalWorkerOptions, version as pdfjsVersion } from 'pdfjs-dist';
import { PDFSinglePageViewer, PDFLinkService, PDFFindController, LinkTarget } from 'pdfjs-dist/legacy/web/pdf_viewer.mjs';

import { createEventBus } from '../../../../utils/pdf-viewer.bus';
import { isTouchDevice, round } from '../../../../utils/helpers';

import type { PDFSource, PDFPageProxy, PDFProgressData, PDFDocumentProxy, PDFDocumentLoadingTask } from '@models/index.model';
import { RenderTextMode } from '@models/index.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'pdf-viewer',
    templateUrl: './pdf-viewer.component.html',
    styleUrls: ['./pdf-viewer.component.scss'],
    standalone: false
})
export class PdfViewerComponent implements OnChanges, OnInit, OnDestroy, AfterViewChecked {
  static readonly CSS_UNITS = 96.0 / 72.0;
  static readonly BORDER_WIDTH = 0;

  @ViewChild('pdfViewerContainer') pdfViewerContainer;

  private isVisible = false;

  private pdfSinglePageViewer: any;
  private pdfSinglePageLinkService: any;
  private pdfSinglePageFindController: any;

  // Se actualiza la URL de los CMaps usando la versión actual
  private _cMapsUrl = `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/cmaps/`;
  private _renderText = true;
  private _renderTextMode: RenderTextMode = RenderTextMode.ENABLED;
  private _stickToPage = false;
  private _originalSize = true;
  private _pdf: PDFDocumentProxy;
  private _page = 1;
  private _zoom = 1;
  private _zoomScale: 'page-height' | 'page-fit' | 'page-width' = 'page-fit';
  private _rotation = 0;
  private _showAll = true;
  private _canAutoResize = true;
  private _fitToPage = false;
  private _externalLinkTarget = 'blank';
  private _showBorders = false;
  private lastLoaded: string | Uint8Array | PDFSource;

  private isInitialized = false;
  private loadingTask: PDFDocumentLoadingTask;
  private destroy$ = new Subject<void>();

  @Output('after-load-complete') afterLoadComplete = new EventEmitter<PDFDocumentProxy>();
  @Output('page-rendered') pageRendered = new EventEmitter<CustomEvent>();
  @Output('pages-initialized') pageInitialized = new EventEmitter<CustomEvent>();
  @Output('text-layer-rendered') textLayerRendered = new EventEmitter<CustomEvent>();
  @Output('error') onError = new EventEmitter<any>();
  @Output('on-progress') onProgress = new EventEmitter<PDFProgressData>();
  @Output() pageChange: EventEmitter<number> = new EventEmitter<number>(true);
  @Input() src: string | Uint8Array | PDFSource;

  @Input('c-maps-url')
  set cMapsUrl(cMapsUrl: string) {
    this._cMapsUrl = cMapsUrl;
  }

  @Input('page')
  set page(_page) {
    _page = parseInt(_page, 10) || 1;
    if (this._pdf) {
      _page = this.getValidPageNumber(_page);
    }
    this._page = _page;
    this.pageChange.emit(_page);
  }

  @Input('render-text')
  set renderText(renderText: boolean) {
    this._renderText = renderText;
  }

  @Input('render-text-mode')
  set renderTextMode(renderTextMode: RenderTextMode) {
    this._renderTextMode = renderTextMode;
  }

  @Input('original-size')
  set originalSize(originalSize: boolean) {
    this._originalSize = originalSize;
  }

  @Input('show-all')
  set showAll(value: boolean) {
    this._showAll = value;
  }

  @Input('stick-to-page')
  set stickToPage(value: boolean) {
    this._stickToPage = value;
  }

  @Input('zoom')
  set zoom(value: number) {
    if (value <= 0) {
      return;
    }
    this._zoom = value;
  }

  get zoom() {
    return this._zoom;
  }

  @Input('zoom-scale')
  set zoomScale(value: 'page-height' | 'page-fit' | 'page-width') {
    this._zoomScale = value;
  }

  get zoomScale() {
    return this._zoomScale;
  }

  @Input('rotation')
  set rotation(value: number) {
    if (!(typeof value === 'number' && value % 90 === 0)) {
      console.warn('Invalid pages rotation angle.');
      return;
    }
    this._rotation = value;
  }

  @Input('external-link-target')
  set externalLinkTarget(value: string) {
    this._externalLinkTarget = value;
  }

  @Input('autoresize')
  set autoresize(value: boolean) {
    this._canAutoResize = Boolean(value);
  }

  @Input('fit-to-page')
  set fitToPage(value: boolean) {
    this._fitToPage = Boolean(value);
  }

  @Input('show-borders')
  set showBorders(value: boolean) {
    this._showBorders = Boolean(value);
  }

  static getLinkTarget(type: string) {
    switch (type) {
      case 'blank':
        return LinkTarget.BLANK;
      case 'none':
        return LinkTarget.NONE;
      case 'self':
        return LinkTarget.SELF;
      case 'parent':
        return LinkTarget.PARENT;
      case 'top':
        return LinkTarget.TOP;
    }
    return null;
  }

  constructor(
    private readonly element: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
    private readonly _translate: TranslateService
  ) {
    let pdfWorkerSrc: string;
    if (
      window.hasOwnProperty('pdfWorkerSrc') &&
      typeof (window as any).pdfWorkerSrc === 'string' &&
      (window as any).pdfWorkerSrc
    ) {
      pdfWorkerSrc = (window as any).pdfWorkerSrc;
    } else {
      pdfWorkerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/legacy/build/pdf.worker.min.mjs`;
    }
    GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
  }

  ngAfterViewChecked(): void {
    if (this.isInitialized) {
      return;
    }
    const offset = this.pdfViewerContainer.nativeElement.offsetParent;
    if (this.isVisible === true && offset == null) {
      this.isVisible = false;
      return;
    }
    if (this.isVisible === false && offset != null) {
      this.isVisible = true;
      setTimeout(() => {
        this.initialize();
        this.ngOnChanges({ src: this.src } as any);
      });
    }
  }

  ngOnInit() {
    this.initialize();
    this.setupResizeListener();
    if (isTouchDevice()) {
      this.setupPinchZoomListener();
    }
  }

  ngOnDestroy() {
    this.clear();
    this.destroy$.next();
    this.loadingTask = null;
  }

  get pdfLinkService(): any {
    return this.pdfSinglePageLinkService;
  }

  get pdfViewer(): any {
    return this.pdfSinglePageViewer;
  }

  get pdfFindController(): any {
    return this.pdfSinglePageFindController;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.isVisible) {
      return;
    }
    // Si cambió la src, cargamos el PDF
    if ('src' in changes) {
      this.loadPDF();
      return;
    }
    if ('zoom' in changes) {
      this.pdfSinglePageViewer.currentScale = this._zoom;
    }
    // Si ya existe el PDF cargado y cambió la página
    if (this._pdf && 'page' in changes) {
      this._page = this.getValidPageNumber(this._page);
      this.pdfSinglePageViewer.currentPageNumber = this._page;
    }

    if (this._pdf) {
      if ('renderText' in changes) {
        this.pdfSinglePageViewer.textLayerMode = this._renderText ? this._renderTextMode : RenderTextMode.DISABLED;
        this.resetPdfDocument();
      } else if ('showAll' in changes) {
        this.resetPdfDocument();
      }
      this.update();
    }
  }

  public updateSize() {
    if (!this._pdf || !this.pdfSinglePageViewer._pages?.length) {
      return;
    }

    from(this._pdf.getPage(this.pdfSinglePageViewer.currentPageNumber) as unknown as Promise<PDFPageProxy>)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page: PDFPageProxy) => {
          const rotation = this._rotation || page.rotate;
          const viewportWidth =
            page.getViewport({ scale: this._zoom, rotation }).width * PdfViewerComponent.CSS_UNITS;
          let scale = this._zoom;
          let stickToPage = true;
          if (
            !this._originalSize ||
            (this._fitToPage && viewportWidth > this.pdfViewerContainer.nativeElement.clientWidth)
          ) {
            const viewPort = page.getViewport({ scale: 1, rotation });
            scale = this.getScale(viewPort.width, viewPort.height);
            stickToPage = !this._stickToPage;
          }
          this.pdfSinglePageViewer.currentScale = scale;
        },
      });
  }

  public clear() {
    if (this.loadingTask && !this.loadingTask.destroyed) {
      this.loadingTask.destroy();
    }
    if (this._pdf) {
      this._pdf.destroy();
      this._pdf = null;
      this.pdfSinglePageViewer.setDocument(null);
      this.pdfSinglePageLinkService.setDocument(null, null);
      this.pdfSinglePageFindController.setDocument(null);
    }
  }

  private getPDFLinkServiceConfig() {
    const pdfLinkServiceConfig: any = {};
    const linkTarget = PdfViewerComponent.getLinkTarget(this._externalLinkTarget);
    if (linkTarget) {
      pdfLinkServiceConfig.externalLinkTarget = linkTarget;
    }
    return pdfLinkServiceConfig;
  }

  private setupSinglePageViewer() {
    const eventBus = createEventBus(this.destroy$);

    fromEvent<CustomEvent>(eventBus, 'pagesinit')
    .pipe(takeUntil(this.destroy$))
    .subscribe(event => {
      this.pageInitialized.emit(event);
      this._page = this.getValidPageNumber(this._page);
      this.pdfSinglePageViewer.currentPageNumber = this._page;
    });

    fromEvent(eventBus, 'pagechanging')
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ pageNumber }): void => {
        if (pageNumber !== this._page) {
          this.page = pageNumber;
        }
      });

    fromEvent<CustomEvent>(eventBus, 'pagerendered')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.pageRendered.emit(event);
      });

    fromEvent<CustomEvent>(eventBus, 'pagesinit')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.pageInitialized.emit(event);
      });

    // fromEvent<CustomEvent>(eventBus, 'textlayerrendered')
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(event => {
    //     this.textLayerRendered.emit(event);
    //   });

    this.pdfSinglePageLinkService = new PDFLinkService({
      eventBus,
      ...this.getPDFLinkServiceConfig(),
    });
    this.pdfSinglePageFindController = new PDFFindController({
      linkService: this.pdfSinglePageLinkService,
      eventBus,
    });
    const pdfOptions: any = {
      eventBus,
      container: this.element.nativeElement.querySelector('div'),
      removePageBorders: !this._showBorders,
      linkService: this.pdfSinglePageLinkService,
      textLayerMode: this._renderText ? this._renderTextMode : RenderTextMode.DISABLED,
      findController: this.pdfSinglePageFindController,
      renderer: 'canvas',
      l10n: undefined,
    };

    this.pdfSinglePageViewer = new PDFSinglePageViewer(pdfOptions);
    this.pdfSinglePageLinkService.setViewer(this.pdfSinglePageViewer);
    this.pdfSinglePageFindController.setDocument(this._pdf);
  }

  private getValidPageNumber(page: number): number {
    if (page < 1) {
      return 1;
    }
    if (page > this._pdf.numPages) {
      return this._pdf.numPages;
    }
    return page;
  }

  private getDocumentParams() {
    const srcType = typeof this.src;
    if (!this._cMapsUrl) {
      return this.src;
    }
    const params: any = {
      cMapUrl: this._cMapsUrl,
      cMapPacked: true,
    };
    if (srcType === 'string') {
      params.url = this.src;
    } else if (srcType === 'object') {
      if ((this.src as any).byteLength !== undefined) {
        params.data = this.src;
      } else {
        Object.assign(params, this.src);
      }
    }
    return params;
  }

  private loadPDF() {
    if (!this.src) {
      return;
    }
    if (this.lastLoaded === this.src) {
      this.update();
      return;
    }
    this.clear();

    this.loadingTask = getDocument(this.getDocumentParams());
    this.loadingTask.onProgress = (progressData: PDFProgressData) => {
      this.onProgress.emit(progressData);
    };
    this.loadingTask.onPassword = (updatePassword: (password: string) => void, reason: any) => {
      if (reason === 1) {
        this.lastLoaded = null;
        this.clear();
        this.onError.emit(this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERRORS.PDF-PASSWORD'));
      }
    };

    const src = this.src;

    from(this.loadingTask.promise as Promise<PDFDocumentProxy>)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async pdf => {
          // Si el PDF tiene protección de edición
          const permissions = await pdf.getPermissions();
          if (permissions !== null) {
            this.lastLoaded = null;
            this.clear();
            this.onError.emit(this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERRORS.PDF-PROTECTED'));
          }
          this._pdf = pdf;
          this.lastLoaded = src;

          this.afterLoadComplete.emit(pdf);
          this.setupSinglePageViewer();
          this.resetPdfDocument();
          this.update();
        },
        error: error => {
          this.lastLoaded = null;
          this.onError.emit(error);
        },
      });
  }

  private update() {
    this.page = this._page;
    this.render();
  }

  private render() {
    this._page = this.getValidPageNumber(this._page);
    if (this._rotation !== 0 || this.pdfSinglePageViewer.pagesRotation !== this._rotation) {
      setTimeout(() => {
        this.pdfSinglePageViewer.pagesRotation = this._rotation;
      });
    }
    if (this._stickToPage) {
      setTimeout(() => {
        this.pdfSinglePageViewer.currentPageNumber = this._page;
      });
    }
    this.updateSize();
  }

  private getScale(viewportWidth: number, viewportHeight: number) {
    const borderSize = this._showBorders ? 2 * PdfViewerComponent.BORDER_WIDTH : 0;
    const pdfContainerWidth = this.pdfViewerContainer.nativeElement.clientWidth - borderSize;
    const pdfContainerHeight = this.pdfViewerContainer.nativeElement.clientHeight - borderSize;
    if (pdfContainerHeight === 0 || viewportHeight === 0 || pdfContainerWidth === 0 || viewportWidth === 0) {
      return 1;
    }
    let ratio = 1;
    switch (this._zoomScale) {
      case 'page-fit':
        ratio = Math.min(pdfContainerHeight / viewportHeight, pdfContainerWidth / viewportWidth);
        break;
      case 'page-height':
        ratio = pdfContainerHeight / viewportHeight;
        break;
      case 'page-width':
      default:
        ratio = pdfContainerWidth / viewportWidth;
        break;
    }
    return (this._zoom * ratio) / PdfViewerComponent.CSS_UNITS;
  }

  private resetPdfDocument() {
    this.pdfFindController.setDocument(this._pdf);
    if (this._showAll) {
      this.pdfSinglePageViewer.setDocument(null);
      this.pdfSinglePageLinkService.setDocument(null, null);
    } else {
      this.pdfSinglePageViewer.setDocument(this._pdf);
      this.pdfSinglePageLinkService.setDocument(this._pdf, null);
    }
  }

  private initialize(): void {
    if (!this.isVisible) {
      return;
    }
    this.isInitialized = true;
    this.setupSinglePageViewer();
  }

  private setupResizeListener(): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(100),
          filter(() => this._canAutoResize && !!this._pdf),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.updateSize();
        });
    });
  }

  private setupPinchZoomListener(): void {
    let initialPinchDistance;
    this.ngZone.runOutsideAngular(() => {
      fromEvent<TouchEvent>(document, 'touchstart')
        .pipe(takeUntil(this.destroy$))
        .subscribe((e: TouchEvent) => {
          if (e.touches.length === 2) {
            initialPinchDistance = this._calculatePinchDistance(e.touches[0], e.touches[1]);
          }
        });
      fromEvent<TouchEvent>(document, 'touchmove')
        .pipe(
          takeUntil(this.destroy$),
          filter(e => initialPinchDistance > 0 && e.touches.length >= 2 && !!e.touches[0] && !!e.touches[1]),
          tap((e: TouchEvent) => e.cancelable && e.preventDefault()),
          map((e: TouchEvent) => this._calculatePinchZoom(e.touches[0], e.touches[1], initialPinchDistance)),
          distinctUntilChanged()
        )
        .subscribe((pinchZoom: number) => {
          if (pinchZoom > 0) {
            this._zoom = Math.min(this._zoom + pinchZoom, 3);
          } else {
            this._zoom = Math.max(this._zoom + pinchZoom, 0);
          }
          this.updateSize();
        });
    });
  }

  private _calculatePinchZoom(firstTouch: Touch, secondTouch: Touch, initialPinchDistance: number): number {
    const pinchDistance = this._calculatePinchDistance(firstTouch, secondTouch);
    const pinchScale = round(pinchDistance / initialPinchDistance, 1);
    return (1 - pinchScale) * -1;
  }

  private _calculatePinchDistance(firstTouch: Touch, secondTouch: Touch): number {
    return Math.hypot(secondTouch.pageX - firstTouch.pageX, secondTouch.pageY - firstTouch.pageY);
  }
}

